/**
 * Calendar UI Slice
 * 
 * Manages UI state for calendar components including:
 * - Expanded event cards
 * - View preferences
 * - UI interactions
 */

import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

interface CalendarUIState {
  expandedEventIds: string[];
}

const initialState: CalendarUIState = {
  expandedEventIds: [],
};

export const calendarUISlice = createSlice({
  name: 'calendarUI',
  initialState,
  reducers: {
    /**
     * Toggle event expanded state
     * If expanded, collapse it. If collapsed, expand it.
     */
    toggleEventExpanded: (state, action: PayloadAction<string>) => {
      const eventId = action.payload;
      const index = state.expandedEventIds.indexOf(eventId);
      
      if (index > -1) {
        // Event is expanded, collapse it
        state.expandedEventIds.splice(index, 1);
      } else {
        // Event is collapsed, expand it
        state.expandedEventIds.push(eventId);
      }
    },

    /**
     * Expand a specific event
     */
    expandEvent: (state, action: PayloadAction<string>) => {
      const eventId = action.payload;
      if (!state.expandedEventIds.includes(eventId)) {
        state.expandedEventIds.push(eventId);
      }
    },

    /**
     * Collapse a specific event
     */
    collapseEvent: (state, action: PayloadAction<string>) => {
      const eventId = action.payload;
      const index = state.expandedEventIds.indexOf(eventId);
      if (index > -1) {
        state.expandedEventIds.splice(index, 1);
      }
    },

    /**
     * Collapse all expanded events
     * Used when navigating to different dates
     */
    collapseAllEvents: (state) => {
      state.expandedEventIds = [];
    },
  },
});

// Actions
export const {
  toggleEventExpanded,
  expandEvent,
  collapseEvent,
  collapseAllEvents,
} = calendarUISlice.actions;

// Selectors
export const selectExpandedEventIds = (state: RootState) => state.calendarUI.expandedEventIds;
export const selectIsEventExpanded = (eventId: string) => (state: RootState) =>
  state.calendarUI.expandedEventIds.includes(eventId);

// Reducer
export default calendarUISlice.reducer;

